import { useTranslations } from 'next-intl';
import { ENTERPRISE_COPY, isEnterpriseDistribution, MODEL_VERSION, SITE } from '@/app/constants';
import type { DisplayModelVersion, I18nRecord, SupportedLanguage } from '@/app/types';
import { createI18nConfig } from '@/app/utils';
import { useLocale } from '@/i18n/routing';

type SpecialModelVersion =
  | typeof MODEL_VERSION.Q3_PRO
  | typeof MODEL_VERSION.Q3_FAST
  | typeof MODEL_VERSION.Q3_LITE
  | typeof MODEL_VERSION.GPT_IMAGE_2
  | typeof MODEL_VERSION.GPT_IMAGE_2_5_FAST
  | typeof MODEL_VERSION.GPT_IMAGE_2_5_PRO
  | typeof MODEL_VERSION.Q3_A
  | typeof MODEL_VERSION.Q3_HH;

type SpecialModelVersionTitleMap = Record<SpecialModelVersion, string>;

const SPECIAL_MODEL_VERSION_TITLE_CONFIG: I18nRecord<SpecialModelVersionTitleMap> =
  createI18nConfig<SpecialModelVersionTitleMap>(
    {
      en: {
        [MODEL_VERSION.Q3_PRO]: 'Nano Banana Pro',
        [MODEL_VERSION.Q3_FAST]: 'Nano Banana 2',
        [MODEL_VERSION.Q3_LITE]: 'Nano Banana 2 Lite',
        [MODEL_VERSION.GPT_IMAGE_2]: 'GPT Image 2',
        [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: 'GPT Image 2.5 Fast',
        [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: 'GPT Image 2.5 Pro',
        [MODEL_VERSION.Q3_A]: 'Omni Video Pro',
        // TODO: 非定制版暂无该模型
        [MODEL_VERSION.Q3_HH]: '',
      },
      zh: {
        [MODEL_VERSION.Q3_PRO]: 'Nano Banana Pro',
        [MODEL_VERSION.Q3_FAST]: 'Nano Banana 2',
        [MODEL_VERSION.Q3_LITE]: 'Nano Banana 2 Lite',
        [MODEL_VERSION.GPT_IMAGE_2]: 'GPT Image 2',
        [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: 'GPT Image 2.5 Fast',
        [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: 'GPT Image 2.5 Pro',
        [MODEL_VERSION.Q3_A]: 'Omni Video Pro',
        // TODO: 非定制版暂无该模型
        [MODEL_VERSION.Q3_HH]: '',
      },
    },
    {
      [MODEL_VERSION.Q3_PRO]: '全能 Q3 Pro',
      [MODEL_VERSION.Q3_FAST]: '全能 Q3 Fast',
      [MODEL_VERSION.Q3_LITE]: '全能 Q3 Lite',
      [MODEL_VERSION.GPT_IMAGE_2]: '全能 Image',
      [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: '全能 Image 2.5 Fast',
      [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: '全能 Image 2.5 Pro',
      [MODEL_VERSION.Q3_A]: '全能 Video Pro',
      // TODO: 非定制版暂无该模型
      [MODEL_VERSION.Q3_HH]: '',
    },
    false,
  );

const getSpecialModelVersionTitle = (modelVersion: SpecialModelVersion, locale: SupportedLanguage): string => {
  // 企业定制版下 Q3_A 模型对外展示为定制版专属名称（Seedance 2.0）
  if (isEnterpriseDistribution && modelVersion === MODEL_VERSION.Q3_A) {
    return ENTERPRISE_COPY.model.q3aName;
  }
  // 企业定制版下 Q3_HH 模型对外展示为定制版专属名称（Happyhorse 1.0）
  if (isEnterpriseDistribution && modelVersion === MODEL_VERSION.Q3_HH) {
    return ENTERPRISE_COPY.model.q3bName;
  }
  return SPECIAL_MODEL_VERSION_TITLE_CONFIG[SITE][locale]?.[modelVersion] ?? '';
};

export const useModelVersionText = () => {
  const t = useTranslations();
  const locale = useLocale();

  const getModelVersionText = ({
    modelVersion,
    transition,
  }: {
    modelVersion: DisplayModelVersion;
    transition?: string;
  }) => {
    if (!modelVersion) return '';

    switch (modelVersion) {
      case MODEL_VERSION.Q3_PRO:
        return getSpecialModelVersionTitle(MODEL_VERSION.Q3_PRO, locale);
      case MODEL_VERSION.Q3_FAST:
        return getSpecialModelVersionTitle(MODEL_VERSION.Q3_FAST, locale);
      case MODEL_VERSION.Q3_LITE:
        return getSpecialModelVersionTitle(MODEL_VERSION.Q3_LITE, locale);
      case MODEL_VERSION.GPT_IMAGE_2:
        return getSpecialModelVersionTitle(MODEL_VERSION.GPT_IMAGE_2, locale);
      case MODEL_VERSION.GPT_IMAGE_2_5_FAST:
        return getSpecialModelVersionTitle(MODEL_VERSION.GPT_IMAGE_2_5_FAST, locale);
      case MODEL_VERSION.GPT_IMAGE_2_5_PRO:
        return getSpecialModelVersionTitle(MODEL_VERSION.GPT_IMAGE_2_5_PRO, locale);
      case MODEL_VERSION.Q3_A:
        return getSpecialModelVersionTitle(MODEL_VERSION.Q3_A, locale);
      case MODEL_VERSION.Q3_HH:
        return getSpecialModelVersionTitle(MODEL_VERSION.Q3_HH, locale);
      case MODEL_VERSION.Q3:
        return 'Vidu Q3';
      case MODEL_VERSION.Q2_PRO:
        return 'Vidu Q2 Pro';
      case MODEL_VERSION.Q2:
        if (transition === 'extendspeed') {
          return t('create.extendForm.modelSelection.q2-speed');
        }
        if (transition === 'extendpro') {
          return t('create.extendForm.modelSelection.q2-pro');
        }
        return 'Vidu Q2';
      case MODEL_VERSION.Q1:
        return 'Vidu Q1';
      default:
        return `Vidu ${modelVersion || '1.0'}`;
    }
  };

  return {
    getModelVersionText,
  };
};
